import { AfterViewInit, Component, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { interval, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FeedActivity, ActivityFeedService, FeedAudience } from '../services/activity-feed.service';
import { FitnessActivityEntity } from '../../../meta-data/app-meta-data.service';
import { userProfilePath } from '../../shared/services/profile-routing/profile-links';
import { DwModalService } from '@devwareapps/devware-cap';
import { openActivityEditor } from './activity-editor.component';
import { take } from 'rxjs/operators';
import { activityDuration } from '../../shared/util/activity-duration';

/** Keep measured space for distant batches, removing their card/avatar DOM. Heights may change freely. */
@Directive({ selector: '[appFeedWindow]', exportAs: 'feedWindow' })
export class FeedWindowDirective implements AfterViewInit, OnDestroy {
    visible = true;
    private observer: IntersectionObserver;
    private height = 0;
    constructor(private element: ElementRef<HTMLElement>, private zone: NgZone) {}
    ngAfterViewInit(): void {
        if (typeof IntersectionObserver === 'undefined') { return; }
        this.observer = new IntersectionObserver(entries => {
            const visible = entries[0].isIntersecting;
            if (visible === this.visible) { return; }
            const node = this.element.nativeElement;
            if (!visible) { this.height = node.getBoundingClientRect().height; }
            this.zone.run(() => { node.style.minHeight = visible ? '' : this.height + 'px'; this.visible = visible; });
        }, { rootMargin: '1200px' });
        this.observer.observe(this.element.nativeElement);
    }
    ngOnDestroy(): void { this.observer?.disconnect(); }
}

@Component({ selector: 'app-activity-feed', templateUrl: './activity-feed.component.html', styleUrls: ['./activity-feed.component.scss'] })
export class ActivityFeedComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() challengeId: number;
    @Input() participantId: number;
    @Input() readOnly = false;
    @Input() title = 'Streaker activity';
    /** Explicit profile/team scope, separate from the viewer who may add or edit activities. */
    @Input() activityParticipantIds: number[];
    search = '';
    searchVisible = false;
    showing: FeedAudience = 'everyone';
    private audienceResolved = false;
    private audienceIds: number[];
    private searchTimer: ReturnType<typeof setTimeout>;
    @ViewChild('sentinel', { static: true }) sentinel: ElementRef<HTMLElement>;
    batches: FeedActivity[][] = [];
    loading = false;
    error = '';
    finished = false;
    private request: Subscription;
    private observer: IntersectionObserver;
    private generation = 0;
    private cursor: FitnessActivityEntity;
    private editorRequest: Subscription;
    editingActivity = false;
    private live = new Subscription();
    constructor(private data: ActivityFeedService, private zone: NgZone, private modal: DwModalService) {
        this.live.add(data.changes$.subscribe(reset => {
            if (reset) { this.refresh(); } else { this.batches = [...this.batches]; }
        }));
        this.live.add(interval(15000).subscribe(() => {
            if (typeof document === 'undefined' || !document.hidden) { this.data.sync(this.challengeId, this.participantId); }
        }));
    }
    openEditor(activity?: FitnessActivityEntity): void {
        if (this.readOnly || this.editingActivity || !this.participantId || activity && !this.isMine(activity)) { return; }
        this.editingActivity = true;
        const challenge = this.challengeId, participant = this.participantId;
        this.editorRequest = openActivityEditor(this.modal, { challengeId: challenge, participantId: participant, activityId: activity?.FitnessActivityId })
            .pipe(take(1)).subscribe(result => {
                this.editingActivity = false;
                // The editor publishes the saved activity ID; the cache reconciles only that row.
            }, () => this.editingActivity = false);
    }
    ngOnChanges(): void { this.refresh(); }
    ngAfterViewInit(): void {
        if (typeof IntersectionObserver !== 'undefined') {
            this.observer = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) { this.zone.run(() => this.loadMore()); }
            }, { rootMargin: '600px' });
            this.observer.observe(this.sentinel.nativeElement);
        }
    }
    refresh(): void {
        clearTimeout(this.searchTimer);
        this.request?.unsubscribe(); this.generation++; this.batches = []; this.cursor = null;
        this.loading = false; this.error = ''; this.finished = false;
        this.audienceResolved = false; this.audienceIds = undefined;
        this.loadMore();
    }
    loadMore(): void {
        if (this.loading || this.finished || !this.challengeId) { return; }
        this.loading = true; this.error = '';
        const generation = this.generation;
        const audience = this.activityParticipantIds !== undefined ? of(this.activityParticipantIds)
            : this.audienceResolved ? of(this.audienceIds) : this.data.loadAudience(this.challengeId, this.participantId, this.showing);
        this.request = audience.pipe(switchMap(ids => {
            this.audienceIds = ids; this.audienceResolved = true;
            return this.data.loadBatch(this.challengeId, this.cursor, this.search, ids, this.participantId);
        })).subscribe(rows => {
            if (generation !== this.generation) { return; }
            this.batches = [...this.batches, rows];
            if (rows.length) { this.cursor = this.data.pageCursor(rows); }
            this.finished = !this.data.hasMore(rows); this.loading = false;
            // Re-observe after layout: short batches may leave the sentinel inside the viewport.
            if (this.observer) { this.observer.unobserve(this.sentinel.nativeElement); this.observer.observe(this.sentinel.nativeElement); }
        }, () => { if (generation === this.generation) { this.loading = false; this.error = 'Activities could not load. Please try again.'; } });
    }
    get hasActivities(): boolean { return this.batches.some(batch => batch.length > 0); }
    dateKey(activity: FitnessActivityEntity): string { return (activity.ActivityDate || '').slice(0, 10); }
    searchChanged(value: string): void {
        this.search = value; clearTimeout(this.searchTimer);
        // Cancel the previous search immediately; debounce only the next request.
        this.request?.unsubscribe(); this.generation++; this.loading = false;
        this.finished = true; // Keep scroll callbacks idle until the debounced search starts.
        this.searchTimer = setTimeout(() => this.refresh(), 300);
    }
    toggleSearch(): void {
        this.searchVisible = !this.searchVisible;
        if (!this.searchVisible) {
            this.search = ''; this.showing = 'everyone'; this.refresh();
        }
    }
    filterChanged(value: FeedAudience): void { this.showing = value; clearTimeout(this.searchTimer); this.refresh(); }
    isMine(activity: FitnessActivityEntity): boolean { return !!this.participantId && activity.UserChallengeId === this.participantId; }
    relativeDate(activity: FitnessActivityEntity, now = new Date()): string {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
        const key = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return this.dateKey(activity) === key(today) ? 'Today' : this.dateKey(activity) === key(yesterday) ? 'Yesterday' : '';
    }
    dateCount(activity: FitnessActivityEntity): string {
        const date = this.dateKey(activity);
        const count = this.batches.reduce((total, batch) => total + batch.filter(row => this.dateKey(row.activity) === date).length, 0);
        const incomplete = !this.finished && this.cursor && this.dateKey(this.cursor) === date;
        return `${count}${incomplete ? '+' : ''} ${count === 1 && !incomplete ? 'activity' : 'activities'}`;
    }
    duration(seconds: number): string {
        return activityDuration(seconds);
    }
    distance(activity: FitnessActivityEntity): string {
        const unit = (activity.DistanceUnitOfMeasure?.UOMName || activity.DistanceUnitOfMeasureIdDisplay || '').toLowerCase();
        const miles = /mile|\bmi\b/.test(unit);
        return `${Number((activity.DistanceMeters / (miles ? 1609.344 : 1000)).toFixed(2))} ${miles ? 'mi' : 'km'}`;
    }
    divider(batch: number, index: number): boolean {
        const previous = index ? this.batches[batch][index - 1] : batch ? this.batches[batch - 1][this.batches[batch - 1].length - 1] : null;
        return !previous || this.dateKey(previous.activity) !== this.dateKey(this.batches[batch][index].activity);
    }
    name(activity: FitnessActivityEntity): string {
        const user = activity.UserChallenge?.UserProfile?.DwUser;
        return [user?.FirstName, user?.LastName].filter(Boolean).join(' ') || 'Streaker';
    }
    profileLink(activity: FitnessActivityEntity): string {
        const slug = activity.UserChallenge?.UserProfile?.ProfileSlug;
        return slug ? userProfilePath(slug) : null;
    }
    trackActivity(index: number, row: FeedActivity): number { return row.activity.FitnessActivityId; }
    ngOnDestroy(): void { this.live.unsubscribe(); clearTimeout(this.searchTimer); this.generation++; this.editorRequest?.unsubscribe(); this.request?.unsubscribe(); this.observer?.disconnect(); }
}
